import * as React from 'react';
import { useEffect, useState, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { FC } from 'react';
import { UserName } from './UserNameComponent';
import {
	contactsRestCrudThunksForBuyer,
	contactsRestCrudThunksForSupplier,
} from '../../../thunks/contacts_thunks';
import { withUserType } from '../../withUserType';
import { ROLE_TYPES } from '../../../utils/DataConstants';

// Request batching manager to prevent duplicate API calls
class ContactRequestBatcher {
	private pendingEmails: Set<string> = new Set();
	private pendingCallbacks: Map<string, Set<() => void>> = new Map();
	private debounceTimer: NodeJS.Timeout | null = null;
	private readonly DEBOUNCE_MS = 100;
	private isProcessing = false;

	constructor(
		private getContactsFn: (emails: string[]) => Promise<any>,
		private userType: string
	) {}

	requestContact(email: string, callback: () => void) {
		// If already in pending, just add callback
		if (this.pendingEmails.has(email)) {
			if (!this.pendingCallbacks.has(email)) {
				this.pendingCallbacks.set(email, new Set());
			}
			this.pendingCallbacks.get(email).add(callback);
			return;
		}

		// Add to pending set
		this.pendingEmails.add(email);
		if (!this.pendingCallbacks.has(email)) {
			this.pendingCallbacks.set(email, new Set());
		}
		this.pendingCallbacks.get(email).add(callback);

		// Clear existing timer
		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer);
		}

		// Set new debounce timer
		this.debounceTimer = setTimeout(() => {
			this.processBatch();
		}, this.DEBOUNCE_MS);
	}

	private async processBatch() {
		if (this.isProcessing || this.pendingEmails.size === 0) {
			return;
		}

		this.isProcessing = true;
		const emailsToFetch = Array.from(this.pendingEmails);
		const allCallbacks = new Set<() => void>();

		// Collect all callbacks
		this.pendingCallbacks.forEach((callbacks) => {
			callbacks.forEach((cb) => allCallbacks.add(cb));
		});

		// Clear pending state
		this.pendingEmails.clear();
		this.pendingCallbacks.clear();
		this.debounceTimer = null;

		try {
			await this.getContactsFn(emailsToFetch);
		} catch (error) {
			console.error('Error fetching contacts:', error);
		} finally {
			// Execute all callbacks
			allCallbacks.forEach((callback) => {
				try {
					callback();
				} catch (error) {
					console.error('Error in contact fetch callback:', error);
				}
			});
			this.isProcessing = false;
		}
	}
}

// Create batcher instances per user type
const batchers = new Map<string, ContactRequestBatcher>();

const getBatcher = (userType: string, getContactsFn: (emails: string[]) => Promise<any>) => {
	if (!batchers.has(userType)) {
		batchers.set(userType, new ContactRequestBatcher(getContactsFn, userType));
	}
	return batchers.get(userType);
};

interface UserNameComponentProps {
	contact: any;
	userType?: 'buyer' | 'supplier';
	mode?: 'card' | 'inline' | 'container' | 'avatar' | 'dropdown';
	nameFormat?: string;
	secondaryField?: string;
	showAvatar?: boolean;
	showNameWithAvatar?: boolean;
	size?: 'default' | 'large' | 'small';
	allowHover?: boolean;
	popoverMode?: 'simple' | 'full';
	placement?: string;
	underline?: boolean;
	details?: any;
	extraDetails?: any;
	renderName?: any;
	onClick?: any;
	account?: any;
	status?: any;
}

interface UserNameWithEmailProps extends Omit<UserNameComponentProps, 'contact'> {
	email: string;
	contact?: any;
	allContacts?: any;
	sessionUserType?: string;
	getContacts?: any;
}

const UserNameWithEmailComponent: FC<UserNameWithEmailProps> = ({
	email,
	contact: providedContact,
	userType,
	allContacts,
	getContacts,
	...otherProps
}) => {
	const [isFetching, setIsFetching] = useState(false);
	const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
	const batcherRef = useRef<ContactRequestBatcher | null>(null);

	const contact = useMemo(() => {
		if (providedContact) {
			return providedContact;
		}

		if (!email) {
			return null;
		}

		if (allContacts && allContacts.records && allContacts.records[email]) {
			return allContacts.records[email];
		}

		return null;
	}, [providedContact, email, allContacts]);

	useEffect(() => {
		// Initialize batcher for this user type
		if (!batcherRef.current && getContacts && userType) {
			batcherRef.current = getBatcher(userType, (emails: string[]) => {
				return getContacts(emails);
			});
		}
	}, [getContacts, userType]);

	useEffect(() => {
		if (!contact && email && !isFetching && !hasAttemptedFetch && batcherRef.current) {
			setIsFetching(true);
			setHasAttemptedFetch(true);

			batcherRef.current.requestContact(email, () => {
				setIsFetching(false);
			});
		} else if (contact) {
			setIsFetching(false);
		}
	}, [email, contact, isFetching, hasAttemptedFetch]);

	useEffect(() => {
		setHasAttemptedFetch(false);
	}, [email]);

	if (contact) {
		return <UserName contact={contact} userType={userType} {...otherProps} />;
	}

	return null;
};

const mapStateToProps = (state: any) => ({
	allContacts: state.all_contacts,
});

const mapDispatchToProps = (dispatch: any, ownProps: any) => ({
	getContacts: (ids) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contactsRestCrudThunksForSupplier.readMultiple(
						ids,
						'workOrdersIndex',
						null,
						null,
						null,
						true
				  )
				: contactsRestCrudThunksForBuyer.readMultiple(
						ids,
						'workOrdersIndex',
						null,
						null,
						null,
						true
				  )
		),
});

export const UserNameWithEmail = withUserType(
	connect(mapStateToProps, mapDispatchToProps)(UserNameWithEmailComponent)
);
